import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GraduationCap, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const registerSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  phone: z.string().min(10, 'Phone number must be at least 10 digits'),
  branch: z.string().min(1, 'Please select a branch'),
});

export default function StudentRegister() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/students/register`, data);
      localStorage.setItem('studentToken', response.data.access_token);
      toast.success('Registration successful! Welcome to Tutor Flex');
      navigate('/student/dashboard');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <GraduationCap className="h-10 w-10 text-brand-blue" />
            <span className="text-3xl font-bold text-brand-dark font-display">Tutor Flex</span>
          </div>
          <h1 className="text-2xl font-bold text-brand-dark">Student Registration</h1>
          <p className="text-slate-600 mt-2">Create your account to start learning</p>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-200">
          <form onSubmit={handleSubmit(onSubmit)} data-testid="student-register-form" className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-brand-dark font-medium">Full Name *</Label>
              <Input
                id="name"
                data-testid="student-name-input"
                {...register('name')}
                placeholder="Enter your full name"
                className="mt-2 rounded-lg"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
            </div>

            <div>
              <Label htmlFor="email" className="text-brand-dark font-medium">Email *</Label>
              <Input
                id="email"
                type="email"
                data-testid="student-reg-email-input"
                {...register('email')}
                placeholder="your.email@example.com"
                className="mt-2 rounded-lg"
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <Label htmlFor="phone" className="text-brand-dark font-medium">Phone Number *</Label>
              <Input
                id="phone"
                data-testid="student-phone-input"
                {...register('phone')}
                placeholder="+91 1234567890"
                className="mt-2 rounded-lg"
              />
              {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>}
            </div>

            <div>
              <Label htmlFor="branch" className="text-brand-dark font-medium">Branch *</Label>
              <Select onValueChange={(value) => setValue('branch', value)}>
                <SelectTrigger data-testid="student-branch-select" className="mt-2 rounded-lg">
                  <SelectValue placeholder="Select your branch" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Computer Science">Computer Science</SelectItem>
                  <SelectItem value="Electronics & Communication">Electronics & Communication</SelectItem>
                  <SelectItem value="Electrical Engineering">Electrical Engineering</SelectItem>
                  <SelectItem value="Instrumentation Engineering">Instrumentation Engineering</SelectItem>
                  <SelectItem value="Other Branches">Other Branches</SelectItem>
                </SelectContent>
              </Select>
              {errors.branch && <p className="text-red-500 text-sm mt-1">{errors.branch.message}</p>}
            </div>

            <div>
              <Label htmlFor="password" className="text-brand-dark font-medium">Password *</Label>
              <Input
                id="password"
                type="password"
                data-testid="student-reg-password-input"
                {...register('password')}
                placeholder="Create a password (min 6 characters)"
                className="mt-2 rounded-lg"
              />
              {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>}
            </div>

            <Button
              type="submit"
              data-testid="student-register-btn"
              disabled={isSubmitting}
              className="w-full bg-brand-dark hover:bg-slate-800 text-white rounded-lg"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-600 text-sm">
              Already have an account?{' '}
              <Link to="/student/login" className="text-brand-blue hover:underline font-medium">
                Login here
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-slate-600 hover:text-brand-blue text-sm">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
